/** @jsxImportSource @emotion/react */
import React from "react";
import Barcoder from "barcoder";
import { css } from "@emotion/react";
import { useTranslation } from "react-i18next";
import formatDate from "../../../../helpers/formatDate";
import logo from "../../../../assets/logo.png";
import useUser from "../../../Shared/UserProvider/useUser";
import { UltyMoney } from "@ulty/ulty-ui";
import { OrderItemQuantity } from "../OrderItemList/OrderItemQuantity";

const styles = {
  page: css`
    width: 600px;
    background: #ffffff;
    color: #111111;
    font-family: Arial, sans-serif;
    padding: 35px 35px 65px 35px;
    box-sizing: border-box;
    position: relative;
  `,
  logo: css`
    position: absolute;
    top: 15px;
    left: 15px;
    width: 50px;
    height: auto;
  `,
  header1: css`
    text-align: center;
    font-size: 8px;
    margin: -10px 0 4px 0;
  `,
  header2: css`
    text-align: center;
    font-size: 12px;
    margin: -10px 0 20px 0;
  `,
  table: css`
    display: flex;
    flex-direction: column;
    font-size: 12px;
  `,
  row: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 5px;
  `,
  rowInner: css`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    font-size: 8px;
    text-indent: 10px;
    line-height: 1.1;
    margin-top: -5px;
    margin-bottom: 2px;
  `,
  headerRow: css`
    font-weight: bold;
    border-bottom: 1px solid #111111;
  `,
  footerRow: css`
    font-weight: bold;
    border-top: 1px solid #111111;
  `,
  description: css`
    width: 60%;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  qty: css`
    width: 40%;
    text-align: right;
  `,
  dates: css`
    margin-top: 10px;
    font-size: 12px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: right;
  `,
};

const ItemQuantity = ({ orderItem }) => {
    const pricedByUnit = orderItem.item?.pricedByUnit
    const sellableUnit = orderItem?.sellableUnit

    const isPricedByWeightOrVolumeAndSoldByCount =
      pricedByUnit?.type !== 'COUNT' &&
      sellableUnit?.type === 'COUNT';
    if(!isPricedByWeightOrVolumeAndSoldByCount) {
    return (<OrderItemQuantity
                          orderItem={orderItem}
                          quantity={orderItem.quantity}
                        />)
  }

  return <>
  <OrderItemQuantity
                          orderItem={{sellableUnit:orderItem.item?.pricedByUnit}}
                          quantity={+(orderItem.totalPrice / orderItem.unitPrice).toFixed(3)}
                        />
                        </>
}

const OrderLine = ({ oi }) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const {user} = useUser();
  const productLabel = oi.name
  const productBarcode = oi.object?.barcode;

  const tags = [
    ...(oi.pickingMethod === "SCAN" ? [t("order.pickup.scanned_label")] : []),
    ...(Barcoder.validate(productBarcode) ? [productBarcode] : []),
    ...(oi.fulfillmentIssue &&
        oi.fulfillmentIssue !== "FOUND_ITEM" &&
        oi.customerAckType !== "AWAITING_ACTION"
          ? [t(`order.fulfillment_issue.${oi.fulfillmentIssue}`)]
          : []),
    ...(oi.customerAckType === "SUBSTITUTION_APPROVED" ? [t(`order.customerAckType.${oi.customerAckType}`)] : []),
  ]
    .filter((item) => item !== undefined && item !== null && item !== false)
    .join(" / ");

  return (
    <>
      <div css={styles.row}>
        <div css={styles.description}>{productLabel}</div>
        <div css={styles.qty}>
           <ItemQuantity orderItem={oi} /> x <UltyMoney
                    readonly
                    inline
                    amount={oi.unitPrice}
                    perUnitOption={oi.item && oi.item.pricedByUnit?.type !== 'COUNT' ? {
                        value: oi.item.pricedByUnit?.unit,
                        text: t(`measurement_unit.symbols.${oi.item.pricedByUnit?.unit}`)
                    }: { value: user.getCompanyCurrency(), text: '' }}
                    currencyCode={user.getCompanyCurrency()}
                    locale={user.getCompanyLocale()}
                  />
        </div>
      </div>
      <div css={styles.rowInner}>
        <div css={styles.description}>{tags}</div>
        <div css={styles.qty}></div>
      </div>
    </>
  );
};

const OrderLineTable = ({ order }) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const {user} = useUser();
  const eligibleItems = order.orderItems.filter((item) => !item.deleted && item.quantity > 0);
  const nbOfProducts = eligibleItems.length;
  const nbOfArticles = eligibleItems.reduce((acc, curr) => acc +  (curr.sellableUnit.type === 'COUNT' ? curr.quantity : 1), 0);
  const countAmount = order.price;

  return (
    <div css={styles.table}>
      <div css={[styles.row, styles.headerRow]}>
        <div css={styles.description}>Description</div>
        <div css={styles.qty}>Qty x P.U.</div>
      </div>

      {eligibleItems.map((item, index) => (
        <OrderLine oi={item} key={item.id ?? item.objectId ?? index} />
      ))}

      <div css={[styles.row, styles.footerRow]}>
        <div css={styles.description}>
          {t("order.pdf.product_count", { count: nbOfProducts })} /{" "}
          {t("order.pdf.article_count", { count: nbOfArticles })}
        </div>
        <div css={styles.qty}><UltyMoney readonly inline amount={countAmount} currencyCode={user.getCompanyCurrency()} locale={user.getCompanyLocale()} /></div>
      </div>
    </div>
  );
};

const OrderDateInfo = ({ order }) => {
  const { t } = useTranslation(undefined, { useSuspense: false });

  return (
    <div css={styles.dates}>
      <div css={{ fontWeight: "bold" }}>{`${order.platform.name} - ${order.displayId}`}</div>
      <div>{t("order.received_at_date", { dt: formatDate(order.receivedAt) })}</div>
      <div>{t("order.accepted_at_date", { dt: formatDate(order.acceptedAt) })}</div>
      <div>{t("order.completed_at_date", { dt: formatDate(order.completedAt) })}</div>
    </div>
  );
};

export const OrderImageDocument = ({ order }) => {
  return (
    <div css={styles.page}>
      <img src={logo} alt="" css={styles.logo} />
      <div css={styles.header1}>{`Ticket ${order.id} - 1 / 1`}</div>
      <div css={styles.header2}>{`${order.platform.name} - ${order.displayId}`}</div>

      <OrderLineTable order={order}/>
      <OrderDateInfo order={order} />
    </div>
  );
};
